import axios from "axios";
import { LoaderFunction, LoaderFunctionArgs, redirect } from "react-router-dom";
import { getHierarchyBaseUrl } from "../../../contexts/hierarchy/getHierarchyBaseUrl";
import { getHierarchyLoaderUrl } from "./hierarchyLoader";

/**
 * Resolves a legacy hierarchy route to the current hierarchy URL structure.
 *
 * The loader builds a hierarchy loader endpoint from the route params
 * (`hierarchyTag` or `studyName` + `reportName`), fetches hierarchy data,
 * then redirects to the canonical path from `getHierarchyBaseUrl()`.
 *
 * If `params.more` and/or the splat wildcard (`params["*"]`) are present, the
 * full trailing path is reconstructed and appended to the redirected URL so that
 * multi-segment deep links are never silently truncated.
 * Existing query parameters are preserved.
 *
 * @param args - Loader arguments from React Router.
 * @param args.params - Legacy route params used to resolve the hierarchy.
 * @param args.request - Navigation request, used to retain query parameters.
 * @returns A redirect response to the canonical hierarchy URL, or `/Corporate`.
 */
export const hierarchyLegacyTagRedirectLoader: LoaderFunction = async ({
  params,
  request,
}: LoaderFunctionArgs) => {
  const url = new URL(request.url);
  const searchParams = new URLSearchParams(url.search);

  // Build the loader URL depending on which route params are present.
  // Report routes carry studyName + reportName; all other hierarchy routes
  // carry a single hierarchyTag.
  // getHierarchyLoaderUrl now always builds a query-string URL (e.g.,
  // Hierarchy/Loader?hierarchyTag=... or Hierarchy/Loader?studyTag=...&reportName=...),
  // which returns the full HierarchyLoaderReturn including taPrefix / projectCode.
  let loaderUrl: string | undefined;
  if (params.studyName !== undefined && params.reportName !== undefined) {
    loaderUrl = getHierarchyLoaderUrl({
      studyTag: params.studyName,
      reportName: params.reportName,
      tocMajorVersion: params.tocMajorVersion,
    });
  } else if (params.hierarchyTag !== undefined) {
    loaderUrl = getHierarchyLoaderUrl({ hierarchyTag: params.hierarchyTag });
  }

  if (loaderUrl !== undefined) {
    const response = await axios.get(loaderUrl);

    /* istanbul ignore else */
    if (response?.data?.hierarchy) {
      // Concatenate params.more and the splat wildcard when both are present so
      // that multi-segment deep links (e.g. /TA/CARDIO/Access/Extra) are fully
      // preserved rather than silently truncated to only the first tail segment.
      const trailingPath =
        params.more !== undefined && params["*"] !== undefined
          ? `${params.more}/${params["*"]}`
          : (params.more ?? params["*"]);
      const newPath =
        getHierarchyBaseUrl(response.data.hierarchy) +
        (trailingPath !== undefined && trailingPath !== "" ? `/${trailingPath}` : "");
      const redirectTo = new URL(url.origin + newPath);
      redirectTo.search = searchParams.toString();
      return redirect(redirectTo.toString());
    }
  }

  return redirect("/Corporate");
};
